import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, Output, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';

import { LayerApi } from '../../../api/layer-api';
import { MapLayerUsageApi } from '../../../api/map-layer-usage-api';
import { IMCMapView } from '../../../classes/mcMapView';
import { environment } from '../../../environment';
import { BooleanConverter, InputConverter } from '../../../inputConverter';
import { CrashAccidentCauseFlags, CrashAccidentTypeFlags } from '../../../selectionOptions';
import { AoiService } from '../../../services/aoi.service';
import { GisToolsService } from '../../../services/gis-tools.service';
import { IAuthenticationService } from '../../../services/interfaces/IAuthenticationService';
import { IUserSettingsService } from '../../../services/interfaces/IUserSettingsService';
import { LayerService } from '../../../services/layer.service';
import { ToSqlStringService } from '../../../services/to-sql-string.service';
import { ICounty, IDistrict, IMapcoreMapImageLayer, IMapcoreProps, IMapQueryConfiguration, IQuery, ISearchCriteria, IUserSetting, MapCoreLayer } from '../../../types';
import { BaseComponent } from '../../base.component';

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-global-filter-ng',
    styleUrls: ['../../sharedStyles/base.scss', '../../sharedStyles/telerik.scss', './global-filter.component.scss'],
    templateUrl: './global-filter.component.html'
})
export class GlobalFilterComponent extends BaseComponent {
    private _active: boolean = false;

    @Input()
    set active(value: boolean) {
        this._active = BooleanConverter(value);
        value ? this.activate() : this.deactivate();
        this.activeChange.emit(value);
    }
    get active(): boolean { return this._active; }

    @Input()
    @InputConverter()
    zoomOnApply: boolean = true;

    @Input() appName: string;
    @Input() queryName = 'mc-global';

    @Output() readonly activeChange: EventEmitter<boolean> = new EventEmitter();

    mapView: NonNullable<__esri.MapView>;
    mapRef: IMCMapView;
    mapId: number;
    userSubscriber: Subscription;
    user = null;
    defaults: IUserSetting[];
    districts: IMultiselectValue[];
    selectedDistricts: string[];
    counties: IMultiselectValue[];
    selectedCounties: string[];
    sapCalendarYears: number[];

    loading = false;

    extraQueryConfigurations: IMapQueryConfiguration[];
    previousExtraQueryConfigurations: IMapQueryConfiguration[];

    allDistricts: IDistrict[] = [];
    allCounties: ICounty[] = [];

    get maxYear() {
        return new Date().getFullYear() + 11;
    }

    get crashAccidentTypeFlagsSelections() {
        return CrashAccidentTypeFlags;
    }
    get crashAccidentCauseFlagsSelections() {
        return CrashAccidentCauseFlags;
    }

    constructor(@Inject('authenticationService') private readonly authService: IAuthenticationService,
        @Inject('UserSettingsService') public UserSettingsService: IUserSettingsService,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly gisToolsService: GisToolsService,
        private readonly aoiService: AoiService,
        private readonly layerService: LayerService,
        private readonly layerApi: LayerApi,
        private readonly mapLayerUsageApi: MapLayerUsageApi,
        private readonly toSqlStringService: ToSqlStringService,
        _vcr: ViewContainerRef) {
        super(_vcr, true);
        this.init();
    }

    private async init() {
        this.mapRef = await this.setupMapViewRef(this.mapView);
    }

    private async activate() {
        if (!this.userSubscriber) {
            this.userSubscriber = await this.authService.GetCurrentUser().subscribe(user => {
                this.user = user;
                if (user && user.id) {
                    this.UserSettingsService.GetSettingValue('userSettings', this.appName, user.emailAddress).subscribe(results => {
                        if (results) {
                            this.defaults = [];
                            if (results.districts) {
                                this.defaults.push({ name: 'districts', value: (<string[]>results.districts) } as IUserSetting);
                            }
                            if (results.counties) {
                                this.defaults.push({ name: 'counties', value: (<string[]>results.counties) } as IUserSetting);
                            }
                            this.useDefaults();
                            this.changeDetectorRef.detectChanges();
                        }
                    });
                }
            });
        }
        this.load();
    }

    private deactivate(): void {
        this.previousExtraQueryConfigurations = this.extraQueryConfigurations;
        this.extraQueryConfigurations = null;
    }

    async load() {
        const dataCalls: Promise<any[]>[] = [
            this.gisToolsService.getAllDistricts('districtNumber,counties{countyCode}'),
            this.gisToolsService.getAllCounties('countyCode,name,districtNumber'),
            // get years based on SAP-PM - CY Plan Snapshot layer
            this.layerService.getDistinctValuesNoQuery(environment.layers.find(layer => layer.name === 'sapCYPlanSnapshot').id, 'CALENDAR_YEAR')
        ];
        [this.allDistricts, this.allCounties, this.sapCalendarYears] = await Promise.all(dataCalls);
        this.districts = this.getDistrictItems();
        this.counties = this.getCountyItems();

        if (this.mapRef.mapcoreViewProps.data) {
            const mapData = JSON.parse(this.mapRef.mapcoreViewProps.data);
            if (mapData && mapData.paramConfig) {
                this.extraQueryConfigurations = mapData.paramConfig as IMapQueryConfiguration[];
                this.setExtraQueryConfigurationDefaults();
            }
        }
        this.changeDetectorRef.detectChanges();
    }

    private setExtraQueryConfigurationDefaults() {
        const remove: IMapQueryConfiguration[] = [];
        const configuredTypes: string[] = [];
        for (const config of this.extraQueryConfigurations) {
            const uniqueType = config.type + (!!config.label ? `-${config.label}` : '');
            if (configuredTypes.includes(uniqueType)) {
                config.duplicateType = true;
            } else {
                configuredTypes.push(uniqueType);
            }
            switch (config.type) {
                case 'CYFY':
                    config.value = 'fy';
                    break;
                case 'SEALCOAT':
                    config.value = 'true';
                    break;
                case 'COMPLETED_VS_SAP':
                    config.value = 'REMOVE';
                    break;
                case 'NOCO_VS_PAYROLL':
                    config.value = 'F';
                    break;
                case 'SAP_CALENDAR_YEAR':
                    config.value = this.sapCalendarYears[this.sapCalendarYears.length - 1].toString();
                    break;
                case 'YEAR':
                    config.value = { operator: '', year: null, yearType: 'cy' };
                    break;
                case 'DATERANGE':
                    config.value = { from: '', to: '' };
                    break;
                case 'CRASHFLAG':
                    config.value = { accidentTypes: [], accidentCauses: [] };
                    break;
                case 'CURRENT_PREDICTED':
                case 'PROJECTED':
                    config.value = { projectedYear: new Date().getFullYear(), which: 'both' };
                    break;
                case 'DEPENDENT_FIELDS':
                    config.value = {
                        primary: [],
                        dependant: []
                    };
                    this.getDistinctValues(config);
                    break;
                case 'GENERIC':
                case 'CONDITION':
                case 'CALCULATED_COLUMN_REPLACEMENT':
                    config.value = [];
                    break;
                case 'CTY':
                    // County Code filter not needed
                    remove.push(config);
                    break;
                case 'BOOLEAN':
                default:
                    console.warn(`Map Filter Configuration ${config.type} Default Values Not Implemented`, config);
                    break;
            }
            config.value = this.previousExtraQueryConfigurationValue(config.type) || config.value;
        }
        if (remove.length) {
            this.extraQueryConfigurations = this.extraQueryConfigurations.filter(config => !remove.includes(config));
        }
    }

    private previousExtraQueryConfigurationValue(type: string) {
        let retVal = null;
        if (this.previousExtraQueryConfigurations) {
            const prev = this.previousExtraQueryConfigurations.find(config => config.type === type);
            if (prev) {
                retVal = prev.value;
            }
        }
        return retVal;
    }

    onSettingChange(setting: string) {
        if (setting === 'districts') {
            this.counties = this.getCountyItems();
        }
        if (['districts', 'counties'].includes(setting) && this.extraQueryConfigurations) {
            for (const config of this.extraQueryConfigurations) {
                if (config.type === 'DEPENDENT_FIELDS') {
                    this.getDistinctValues(config);
                }
            }
        }
        this.changeDetectorRef.detectChanges();
    }

    useDefaults() {
        if (this.defaults) {
            for (const setting of this.defaults) {
                switch (setting.name) {
                    case 'districts':
                        this.selectedDistricts = setting.value;
                        this.counties = this.getCountyItems();
                        break;
                    case 'counties':
                        this.selectedCounties = setting.value;
                        break;
                    default:
                        break;
                }
                this.onSettingChange(setting.name);
            }
        }
    }

    @Input()
    beforeApply: () => Promise<void>;

    async apply() {
        this.loading = true;
        if (this.beforeApply) {
            await this.beforeApply();
        }
        this.aoiService.clearSearchCriterias();
        const searchCriteria: ISearchCriteria[] = [];
        const counties = this.getCountiesFromSelections();
        if (counties.length) {
            searchCriteria.push({
                type: 'county',
                countyCodes: counties
            });
        } else {
            searchCriteria.push({ type: 'state' });
        }
        const mapCoreViewProps = this.mapRef.mapcoreViewProps;
        const isSystemMap = mapCoreViewProps.isSystem;
        if (isSystemMap) {
            const districts = this.getDistrictFromSelections();
            if (districts && districts.length && districts.length > 0) {
                districts.forEach(district => {
                    this.mapLayerUsageApi.saveMapUsage(mapCoreViewProps.id, JSON.stringify(searchCriteria), district);
                });
            }
        }
        this.aoiService.setSearchCriterias(searchCriteria);
        const filteredLayers = await this.aoiService.showBySearchCriterias(true, this.zoomOnApply, true, true, true) as MapCoreLayer[];
        if (this.extraQueryConfigurations) {
            this.syncExtraQueryConfigurationValues();
            const queryLayerIds = this.extraQueryConfigurations.map(qc => qc.layers).flat();
            const filterLayers = this.layerService.mapLayers.mapImage.filter((layer: any) => {
                return layer.mapcoreProps &&
                    (queryLayerIds.includes((<IMapcoreProps>layer.mapcoreProps).id) ||
                    queryLayerIds.includes((<IMapcoreProps>layer.mapcoreProps).parentId) ||
                    queryLayerIds.includes((<IMapcoreProps>layer.mapcoreProps).idFromSavedLayer)
                    );
            }) as IMapcoreMapImageLayer[];
            this.clearFilter(filterLayers);
            this.filterLayers(filterLayers);
        }
        if (searchCriteria[0].type === 'state' && filteredLayers && filteredLayers.length) {
            this.layerService.zoomToLayer(filteredLayers.map(layer => layer.id));
        }
        this.loading = false;
        this.active = false;
    }

    private syncExtraQueryConfigurationValues() {
        for (const config of this.extraQueryConfigurations.filter(eqc => eqc.duplicateType)) {
            const mainEQC = this.extraQueryConfigurations.find(eqc => eqc.type === config.type);
            config.value = mainEQC.value;
        }
    }

    private getCountiesFromSelections() {
        const retVal: string[] = [];
        if ((this.selectedDistricts && this.selectedDistricts.length) || (this.selectedCounties && this.selectedCounties.length)) {
            if (this.selectedDistricts && this.selectedDistricts.length && (!this.selectedCounties || !this.selectedCounties.length)) {
                this.selectedCounties = this.getCountiesFromSelectedDistricts().map(county => `county-${county.countyCode}`);
            }
            if (this.selectedCounties && this.selectedCounties.length) {
                retVal.push(...this.selectedCounties.map(county => county.replace('county-', '')));
            }
        }
        return retVal;
    }

    private getDistrictFromSelections() {
        const retVal = [];
        if (this.selectedDistricts && this.selectedDistricts.length && this.selectedDistricts.length > 0) {
            this.selectedDistricts.forEach(selectedDistrict => {
                const districtNumber = selectedDistrict.replace('district-', '');
                if (!retVal.includes(parseInt(districtNumber, 0))) {
                    retVal.push(parseInt(districtNumber, 0));
                }
            });
        }
        if (this.selectedCounties && this.selectedCounties.length && this.selectedCounties.length > 0) {
            const counties = this.selectedCounties.map(countyStr => {
                const ctyCode = countyStr.replace('county-', '');
                const matchingCounty = this.allCounties.find(county => county.countyCode.toString() === ctyCode);
                return matchingCounty;
            });
            if (counties && counties.length && counties.length > 0) {
                counties.forEach(county => {
                    if (!retVal.includes(parseInt(county.districtNumber, 0))) {
                        retVal.push(parseInt(county.districtNumber, 0));
                    }
                });
            }
        }
        return retVal;
    }

    private clearFilter(filterLayers: IMapcoreMapImageLayer[]) {
        for (const layer of filterLayers) {
            if (layer.mapcoreProps.query) {
                if (layer.mapcoreProps.query.name === this.queryName) {
                    layer.mapcoreProps.query = {};
                    layer.mapcoreProps.needsRefresh = true;
                } else if (layer.mapcoreProps.query.subqueries && layer.mapcoreProps.query.subqueries.length && layer.mapcoreProps.query.subqueries.some(x => x.name === this.queryName)) {
                    layer.mapcoreProps.query.subqueries = layer.mapcoreProps.query.subqueries.filter(x => x.name !== this.queryName);
                    layer.mapcoreProps.needsRefresh = true;
                }
            }

        }
    }

    private buildQuery(config: IMapQueryConfiguration) {
        const query: IQuery = {};
        if (config.type === 'CYFY') {
            const filterParts = config.filter[config.value].split(' ').map(fp => fp.trim());
            query.field = filterParts[0];
            query.operator = filterParts[1] ? filterParts[1] : '=';
            query.value = filterParts[2] ? filterParts[2] : '';
        } else if (config.filter) {
            query.subqueries = [];
            for (const filter of Object.keys(config.filter)) {
                const filterParts = config.filter[filter].split(' ').map(fp => fp.trim());
                query.subqueries.push({
                    field: filterParts[0],
                    operator: filterParts[1] ? filterParts[1] : '=',
                    value: filterParts[2] ? filterParts[2] : config.value.toString()
                });
            }
        } else if (config.type === 'SEALCOAT') {
            this.updateQuery(query, config);
        } else if (config.type === 'CRASHFLAG') {
            query.operator = 'OR';
            query.subqueries = [];
            for (const type of config.value.accidentTypes) {
                query.subqueries.push({ field: type, operator: '=', value: '1' });
            }
            for (const cause of config.value.accidentCauses) {
                query.subqueries.push({ field: cause, operator: '=', value: '1' });
            }
        } else if (config.type === 'CURRENT_PREDICTED') {
            query.field = config.field;
            query.operator = '=';
            query.value = config.value.projectedYear ? config.value.projectedYear.toString() : null;
        } else if (config.type === 'PROJECTED') {
            query.operator = 'AND';
            query.subqueries = [];
            query.subqueries.push({
                field: config.field,
                operator: '=',
                value: config.value.projectedYear ? config.value.projectedYear.toString() : null
            });
        } else if (config.type === 'SAP_CALENDAR_YEAR' && config.value) {
            const longYear = config.value.toString();
            const shortYear = config.value.toString().slice(-2);
            const previousShortYear = (config.value - 1).toString().slice(-2);

            const calendarYearEqualFilter = {
                field: 'CALENDAR_YEAR',
                operator: '=',
                value: longYear
            };

            const calendarYearGreaterEqualFilter = {
                field: 'CALENDAR_YEAR',
                operator: '>=',
                value: longYear
            };

            const payrollDateFilter = {
                field: 'PAYROLL_DATE',
                operator: 'BETWEEN',
                value: `${longYear}0101,${longYear}1231`
            };

            const revisionFilter = {
                field: 'REVISION',
                operator: 'IN',
                value: `${previousShortYear}-3,${shortYear}-1`
            };

            const plannedRevisionFilter = {
                field: 'REVISION',
                operator: 'IN',
                value: `${previousShortYear}-2,${shortYear}-2`
            };

            query.subqueries = [calendarYearEqualFilter, calendarYearGreaterEqualFilter, payrollDateFilter, revisionFilter, plannedRevisionFilter];
        } else if (config.value && !['DEPENDENT_FIELDS'].includes(config.type)) {
            this.dependantFieldsQuery(config, query);
        }
        this.setQueryName(query);
        return query;
    }

    private filterLayers(filterLayers: IMapcoreMapImageLayer[]) {
        for (const config of this.extraQueryConfigurations.filter(qc => qc.value && qc.value !== 'null')) {
            const query = this.buildQuery(config);
            for (const layer of filterLayers.filter(fl => config.layers.includes(fl.mapcoreProps.id) || config.layers.includes(fl.mapcoreProps.parentId) || config.layers.includes(fl.mapcoreProps.idFromSavedLayer))) {
                this.layerService.updateLayerName(config, layer.mapcoreProps);
                const filtered = this.configureLayerForColumnMap(config, layer);
                if (query && ((query.subqueries && query.subqueries.length) || (query.field && (query.value !== undefined && query.value !== '')))) {
                    const layerQuery = { ...query } as IQuery;
                    if (config.type === 'CURRENT_PREDICTED') {
                        if (layer.mapcoreProps.name.toUpperCase().includes('(CURRENT)')) {
                            layerQuery.operator = '<';
                        } else if (layer.mapcoreProps.name.toUpperCase().includes('(PROJECTED)')) {
                            layerQuery.operator = '>=';
                        }
                        if (config.compareFullDate) {
                            layerQuery.value = this.getFullDate(layerQuery.value);
                        }
                    }
                    if (config.type === 'PROJECTED') {
                        this.projectedFilter(layerQuery, config, layer.mapcoreProps.name.toUpperCase());
                    }
                    if (config.type === 'CYFY') {
                        delete layerQuery.name;
                    }
                    if (config.type === 'SAP_CALENDAR_YEAR' && config.value) {
                        this.sapCalendarFilter(layerQuery, layer.mapcoreProps.name.toUpperCase());
                    }
                    this.updateLayerFilter(layer, config, layerQuery);
                } else if (filtered || layer.mapcoreProps.needsRefresh) {
                    this.layerService.updateLayerFilter(layer, layer.mapcoreProps.query);
                    layer.mapcoreProps.needsRefresh = false;
                }
            }
        }
        this.layerService.refreshLayers();
    }

    private updateLayerFilter(layer: IMapcoreMapImageLayer, config: IMapQueryConfiguration, layerQuery: IQuery) {
        if (layer.mapcoreProps.query) {
            let fields: string[] = null;
            if (config.type === 'CYFY') {
                fields = Object.values(config.filter).map(f => f.split(' ')[0].trim().toUpperCase());
            }
            if (config.type === 'SAP_CALENDAR_YEAR' && config.value) {
                fields = layerQuery.subqueries.map(subq => subq.field.toUpperCase());
            }
            let toRemove = null;
            if (layer.mapcoreProps.query.subqueries) {
                toRemove = layer.mapcoreProps.query.subqueries.filter(subquery => {
                    if (fields && fields.includes(subquery.field.toUpperCase())) {
                        if (layerQuery.value === 'XX') {
                            layerQuery.value = subquery.value;
                        }
                        return true;
                    } else if (config.field) {
                        if (config.type === 'PROJECTED' && subquery.subqueries) {
                            return true;
                        } else {
                            return subquery.field ? subquery.field.toUpperCase() === config.field.toUpperCase() : false;
                        }
                    } else if (config.filter) {
                        return Object.keys(config.filter).map(x => x.toUpperCase()).includes(subquery.field.toUpperCase());
                    }
                    return false;
                });
                if (toRemove.length) {
                    for (const remove of toRemove) {
                        layer.mapcoreProps.query.subqueries.splice(layer.mapcoreProps.query.subqueries.indexOf(remove), 1);
                    }
                }
            } else {
                if (fields && layer.mapcoreProps.query.field && fields.includes(layer.mapcoreProps.query.field.toUpperCase())) {
                    if (layerQuery.value === 'XX') {
                        layerQuery.value = layer.mapcoreProps.query.value;
                    }
                    toRemove = true;
                } else if (config.field) {
                    toRemove = layer.mapcoreProps.query.field ? layer.mapcoreProps.query.field.toUpperCase() === config.field.toUpperCase() : false;
                } else if (config.filter && layer.mapcoreProps.query.field) {
                    toRemove = Object.keys(config.filter).map(x => x.toUpperCase()).includes(layer.mapcoreProps.query.field.toUpperCase());
                } else {
                    toRemove = false;
                }
                if (toRemove) {
                    layer.mapcoreProps.query = {};
                }
            }
        }
        if (layerQuery.value !== 'REMOVE') {
            const finalFilter = this.createFinalFilter(layerQuery, layer);
            this.layerService.updateLayerFilter(layer, finalFilter);
        }
    }

    private createFinalFilter(layerQuery: IQuery, layer: IMapcoreMapImageLayer) {
        let finalFilter: IQuery;
        if (layerQuery.operator !== 'OR' && layerQuery.subqueries && layerQuery.subqueries.length) {
            for (const query of layerQuery.subqueries) {
                if (!finalFilter) {
                    finalFilter = this.toSqlStringService.addFilter(layer.mapcoreProps.query, query);
                } else {
                    finalFilter = this.toSqlStringService.addFilter(finalFilter, query);
                }
            }
        } else {
            finalFilter = this.toSqlStringService.addFilter(layer.mapcoreProps.query, layerQuery);
        }
        return finalFilter;
    }

    private projectedFilter(query: IQuery, config: IMapQueryConfiguration, layerName: string) {
        const currentYear = new Date().getFullYear().toString();
        const projectedYear = query.subqueries[0].value.toString();
        // Clearing filter options and updating based on date or year config.
        query.subqueries = [];

        if (layerName.toUpperCase().includes('(CURRENT)')) {
            if (config.compareFullDate) {
                query.operator = 'AND';
                query.subqueries.push({
                    field: config.field,
                    operator: '<=',
                    value: this.getFullDate(currentYear)
                });
            }
        } else {
            query.operator = 'AND';
            query.subqueries.push({
                field: config.field,
                operator: '>=',
                value: config.compareFullDate ? this.getFullDate(currentYear) : currentYear
            });
            query.subqueries.push({
                field: config.field,
                operator: '<=',
                value: config.compareFullDate ? this.getEndDayOfYear('-', projectedYear) : projectedYear
            });
        }
    }

    private sapCalendarFilter(query: IQuery, layerName: string) {
        const tempSubquery = [];
        if (layerName.toUpperCase().includes('COMPLETED')) {
            // payroll date
            tempSubquery.push(query.subqueries[2]);
        } else if (layerName.toUpperCase().includes('CURRENT')) {
            // calendar year greater or equal
            tempSubquery.push(query.subqueries[1]);
            // revision
            tempSubquery.push(query.subqueries[3]);
        } else if (layerName.toUpperCase().includes('ORIGINAL')) {
            // calendar year equal
            tempSubquery.push(query.subqueries[0]);
        } else if (layerName.toUpperCase().includes('ACTIVITIES')) {
            // calendar year greater or equal
            tempSubquery.push(query.subqueries[1]);
            // planned revision
            tempSubquery.push(query.subqueries[4]);
        }
        if (tempSubquery.length) {
            query.subqueries = tempSubquery;
        }
    }

    private getEndDayOfYear(delimiter = '', year) {
        return [year, '12', '31'].join(delimiter);
    }

    private configureLayerForColumnMap(config: IMapQueryConfiguration, layer: IMapcoreMapImageLayer) {
        let retVal = false;
        if (config.columnMap) {
            const id = layer.mapcoreProps.parentId || layer.mapcoreProps.id;
            const ids = Object.keys(config.columnMap).map(Number);
            if (ids.includes(id)) {
                const columnMap = config.columnMap[id];
                if (config.value.primary.length) {
                    layer.mapcoreProps.query = this.toSqlStringService.addFilter(layer.mapcoreProps.query, {
                        field: columnMap.fieldName,
                        operator: 'IN',
                        value: config.value.primary.join(','),
                        name: this.queryName
                    });
                    retVal = true;
                }
                if (columnMap.dependentFieldName && config.value.dependant.length) {
                    layer.mapcoreProps.query = this.toSqlStringService.addFilter(layer.mapcoreProps.query, {
                        field: columnMap.dependentFieldName,
                        operator: 'IN',
                        value: config.value.dependant.join(','),
                        name: this.queryName
                    });
                    retVal = true;
                }
            }
        }
        return retVal;
    }

    cancel() {
        this.active = false;
    }

    getDistrictItems() {
        return this.allDistricts.map(district => ({
            name: `district-${district.districtNumber}`,
            label: `District ${district.districtNumber}`,
            value: district.districtNumber
        }));
    }

    getCountyItems() {
        const availableCounties = this.getCountiesFromSelectedDistricts() || this.allCounties;
        return availableCounties.map(county => ({
            name: `county-${county.countyCode}`,
            label: county.name,
            value: county.countyCode
        }));
    }

    getCountiesFromSelectedDistricts() {
        let retVal: ICounty[] = null;
        if (this.selectedDistricts && this.selectedDistricts.length) {
            const districts = this.allDistricts.filter(dis => this.selectedDistricts.map(v => v.replace('district-', '')).includes(dis.districtNumber));
            retVal = this.allCounties.filter(cty => districts.map(dis => dis.counties.map(c => c.countyCode)).flat().includes(cty.countyCode));
        }
        return retVal;
    }

    private async getDistinctValues(config: IMapQueryConfiguration) {
        const primaryTable = config.primaryOptionsTableNames[0].split('.');
        const dependantTable = config.dependentOptionsTableNames[0].split('.');
        let query: IQuery = null;
        const counties = this.getCountiesFromSelections();
        if (counties.length) {
            query = {
                field: config.countyOptionsColName,
                operator: 'IN',
                value: counties.join(',')
            };
        }
        [config.options, config.dependentOptions] = await Promise.all([
            this.layerApi.getColumnDistinctValues(primaryTable[1], primaryTable[0], config.field, query),
            this.layerApi.getColumnDistinctValues(dependantTable[1], dependantTable[0], config.dependentField, query)
        ]); // TODO: May need to get distinct from multiple primary or multiple dependant tables

    }

    private getFullDate(year: number | string) {
        const fullDate = new Date();
        fullDate.setFullYear(Number(year));
        return `${fullDate.getFullYear()}-${(fullDate.getMonth() + 1).toString().padStart(2, '0')}-${fullDate.getDate().toString().padStart(2, '0')}`;
    }

    private updateQuery(query: IQuery, config: IMapQueryConfiguration) {
        query.field = config.field;
        query.value = '';
        if (config.value === 'false') {
            //is MPMS
            if (config.label.indexOf('MPMS') > -1) {
                query.operator = 'NOT IN';
                query.value = '00037';
            } else if (config.label.indexOf('SAP')) {
                query.operator = 'NOT IN';
                query.value = '712401,712404';
            } else {
                query.operator = 'IS NULL';
            }
        } else {
            query.operator = 'IS NOT NULL';
        }
    }

    private dependantFieldsQuery(config: IMapQueryConfiguration, query: IQuery) {
        if (Array.isArray(config.value) && config.value.length) {
            query.field = config.field;
            query.operator = 'IN';
            query.value = config.value.join(',');
        } else {
            query.field = config.field;
            query.operator = '=';
            query.value = config.value ? config.value.toString() : null;
        }
    }

    private setQueryName(query: IQuery) {
        if (query.field) {
            query.name = this.queryName;
        } else if (query.subqueries && query.subqueries.length) {
            query.subqueries.forEach(sq => sq.name = this.queryName);
        }
    }
}

interface IMultiselectValue {
    name: string;
    label: string;
    value: any;
}
